
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage, ChatMessageProps } from './ChatMessage';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export interface ChatMessageData extends Omit<ChatMessageProps, 'userInitials'> {
  timestamp: string;
}

interface PromptChatProps {
  chatMessages: ChatMessageData[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessageData[]>>;
  userInitials: string;
  handleNewChallenge?: () => void;
  isChildComponent?: boolean;
}

export const PromptChat = ({ 
  chatMessages, 
  setChatMessages, 
  userInitials,
  handleNewChallenge,
  isChildComponent = false
}: PromptChatProps) => {
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;
    
    const newMessage: ChatMessageData = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages([...chatMessages, newMessage]);
    setMessage('');
    setIsLoading(true);
    
    try {
      const lastAssistantMessage = [...chatMessages]
        .reverse()
        .find(msg => msg.role === 'assistant');
      
      const challengeDescription = lastAssistantMessage?.content || "AI prompt practice challenge";
      
      const previousMessages = chatMessages
        .filter(msg => msg.content !== challengeDescription)
        .slice(-5)
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));
      
      const { data, error } = await supabase.functions.invoke('generate-practice-feedback', {
        body: {
          userPrompt: newMessage.content,
          challengeDescription,
          previousMessages
        }
      });
      
      if (error) {
        console.error('Error calling Edge Function:', error);
        throw new Error('Failed to generate feedback. Please try again.');
      }
      
      const aiResponse: ChatMessageData = {
        role: 'assistant',
        content: data.feedback,
        timestamp: new Date().toISOString()
      };
      
      setChatMessages(prevMessages => [...prevMessages, aiResponse]);
    } catch (error) {
      console.error('Error generating response:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate feedback',
        variant: 'destructive'
      });
      
      const errorResponse: ChatMessageData = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error while generating feedback. Please try again or contact support if the issue persists.",
        timestamp: new Date().toISOString()
      };
      
      setChatMessages(prevMessages => [...prevMessages, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex-grow flex flex-col space-y-4">
      <div className="flex-grow overflow-auto space-y-4 max-h-[500px] p-4 bg-secondary/30 rounded-lg">
        {chatMessages.map((msg, index) => (
          <ChatMessage
            key={index}
            role={msg.role}
            content={msg.content}
            userInitials={userInitials}
          />
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex">
              <div className="flex max-w-[80%] flex-row">
                <Avatar className="h-8 w-8 mr-2">
                  <AvatarFallback>AI</AvatarFallback>
                </Avatar>
                <div className="px-4 py-3 rounded-lg bg-muted flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <p className="text-sm">Generating response...</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="relative">
        <Input
          placeholder="Type your prompt here..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          className="pr-10"
          disabled={isLoading}
        />
        <Button
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
          onClick={handleSendMessage}
          disabled={!message.trim() || isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};
